<script setup lang="ts">
import debounce from 'lodash.debounce';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import type { QInput } from 'quasar';

import { errorMessage, successMessage } from '@/composables/Notify.ts';
import { captureError } from '@/composables/Sentry.ts';
import { MAX_NAME_SIZE } from '@/composables/utils.ts';
import { mixedIds } from '@/utils/DataTestIds.ts';

import { useAssetCardStoreV2 } from '@/stores/v2/AssetCardStore.ts';
import { useProjectsStoreV2 } from '@/stores/v2/ProjectStore.ts';
import { useTestingStore } from '@/stores/v2/TestingStore.ts';

import { editOutlined } from '@/assets/icons/materialSymbols/MaterialSymbols.ts';

import AIconButton from '@/components/atoms/AIconButton/AIconButton.vue';

const projectsStoreV2 = useProjectsStoreV2();
const assetsStore = useAssetCardStoreV2();
const testingStore = useTestingStore();
const route = useRoute();
const { t } = useI18n();

const routeName = route.name;

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  hasPermission: {
    type: Boolean,
    default: false,
  },
});

const name = ref('');
const nameInput = ref<QInput | null>(null);
const contentEditable = ref(false);
const validationPassed = ref(true);

const debouncedValidation = debounce(() => {
  if (!nameInput.value) return;
  const isNameValid = nameInput.value.validate();

  if (isNameValid) {
    validationPassed.value = true;
  } else {
    validationPassed.value = false;
  }
}, 100);

async function updateTitle() {
  try {
    let data;
    switch (routeName) {
      case 'project-dashboard': {
        if (!projectsStoreV2.project) return;

        data = await projectsStoreV2.updateProject({
          projectId: projectsStoreV2.project.id,
          projectUpdate: {
            name: name.value,
          },
        });

        successMessage(t('AI System {name} was updated', { name: data?.name ?? '' }));

        break;
      }

      case 'asset-detail':
        if (!assetsStore.currentAsset) return;
        data = await assetsStore.updateAssetCard({
          assetCardId: assetsStore.currentAsset.id,
          updateAssetCardRequest: {
            name: name.value,
          },
        });

        successMessage(t('Asset {name} was updated', { name: data?.name ?? '' }));

        break;
      case 'test-detail': {
        if (!testingStore.currentTestingTest || !projectsStoreV2.project) return;

        data = await testingStore.updateTestingTest({
          projectId: projectsStoreV2.project.id,
          testId: testingStore.currentTestingTest.id,
          updateTestingTestRequest: {
            ...testingStore.currentTestingTest,
            name: name.value,
          },
        });

        successMessage(t('Test {name} was updated', { name: data?.name ?? '' }));

        break;
      }
    }
    contentEditable.value = false;
  } catch (error) {
    errorMessage(error);

    captureError(error, {
      message: 'Component: PageTitle, Function: updateTitle',
      data: {
        name: name.value,
      },
    });
  }
}

watch(
  () => props.title,
  (newValue) => {
    name.value = newValue;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="title__container row">
    <h1 v-if="!contentEditable" :data-test-id="mixedIds.pageTitle">{{ title }}</h1>
    <AIconButton
      v-if="!contentEditable && hasPermission"
      :icon="editOutlined"
      :disable="!hasPermission"
      :data-test-id="mixedIds.editTitleButton"
      @click="contentEditable = true"
    >
      <q-tooltip v-if="!hasPermission" anchor="top middle" self="bottom middle">
        {{ t("You don't have permission. Contact the admin.") }}
      </q-tooltip>
    </AIconButton>
    <div class="content col-12">
      <q-input
        v-if="contentEditable"
        ref="nameInput"
        v-model="name"
        outlined
        dense
        :placeholder="t('Enter new name...')"
        no-error-icon
        :rules="[
          (val: string) => (val && val.length > 0) || t('Please type something'),
          (val: string) =>
            (val && val.length <= MAX_NAME_SIZE) ||
            t('Maximum {max} characters allowed', { max: MAX_NAME_SIZE }),
        ]"
        @update:model-value="debouncedValidation"
      />

      <div v-if="contentEditable" class="button__container row items-center">
        <AIconButton
          icon="check"
          :disable="!validationPassed"
          class="q-mr-md"
          @click="updateTitle"
        />
        <AIconButton icon="close" @click="contentEditable = false" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title__container {
  position: relative;
  // Truncates instead of widening its flex row.
  min-width: 0;

  h1 {
    min-width: 0;
    @include heading-04(600, var(--color-text-heading));
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
    margin: 0;

    @include respond-to-min(sm) {
      @include heading-03(600, var(--color-text-heading));
    }
  }
  :deep(label) {
    input {
      @include heading-02(600, var(--color-text-heading));
    }
    .q-field__control:before {
      border: 1px solid $secondary-200 !important;
    }
  }
  .content {
    .button__container {
      position: absolute;
      right: 0;
      bottom: -25px;
    }
  }
}
</style>
